/**
 * Inquiry Agent – Dubai Police 999 Emergency Assistant
 *
 * Answer-only agent. Does NOT detect intent or agencies — that was done by IntentAgent.
 *
 * Responsibilities:
 *   1. Search the knowledge base (RAG)
 *   2. Answer the user's question concisely
 *   3. Ask 1-2 follow-up questions for active incidents (never for hypothetical)
 *   4. Call done_answering() when finished
 *      - If recommendedAgencies is non-empty → routes to image_upload_agent
 *      - If recommendedAgencies is empty → routes to closing_agent
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';
import { searchKnowledgeBase, formatKBResults } from '../../utils/knowledge-base-search';

export function createInquiryAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'inquiry_agent',
    name: 'Knowledge & Response Agent',
    description: 'Searches KB, answers questions, asks follow-up Qs, then hands off based on pre-detected intent',

    initialPrompt: 'IMMEDIATELY call search_knowledge_base with the pending_question from context and the user\'s language. Answer clearly in 2-4 sentences. Ask at most 2 relevant follow-up questions for active incidents. Then call done_answering().',

    entryGuardrails: requireLanguage(),

    exitGuardrails: {
      transitions: new Map([
        ['to_image_upload', { target: { agentId: 'image_upload_agent' }, guardrails: [languageDetectedGuardrail] }],
        ['to_closing', { target: { agentId: 'closing_agent' }, guardrails: [languageDetectedGuardrail] }],
      ]),
      fallback: { agentId: 'closing_agent' },
    },

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {

      // ─── search_knowledge_base ──────────────────────────────────────────
      if (toolName === 'search_knowledge_base' || toolName === 'search_faq') {
        const question = (args.question || args.query || context.transitionData.pendingQuestion || '').trim();
        const language = (args.language || context.transitionData.language || 'en') as 'ar' | 'en' | 'ur';

        if (!question) {
          return {
            toolName, args,
            result: {
              success: false,
              error: 'No question provided to search_knowledge_base',
            },
          };
        }

        console.log(
          `%c[InquiryAgent] KB SEARCH: "${question.slice(0, 80)}" (lang: ${language})`,
          'color: #00bcd4; font-weight: bold'
        );

        try {
          const results = await searchKnowledgeBase(question, 5, language);
          const formatted = formatKBResults(results, language);

          console.log(`%c[InquiryAgent] Retrieved ${results.length} KB entries`, 'color: #4caf50; font-weight: bold');
          results.forEach((r, i) => {
            const score = Math.round(r.similarity_score * 100);
            console.log(`  [${i + 1}] ${score}% | ${r.category} | ${r.question.slice(0, 60)}`);
          });

          return {
            toolName, args,
            result: {
              success: true,
              results_count: results.length,
              knowledge_base_context: formatted,
              message: results.length > 0
                ? `Retrieved ${results.length} relevant KB entries. Use the 'knowledge_base_context' to formulate your answer in the user's language. Then ask 1-2 follow-up questions if this is an active incident. Then call done_answering().`
                : 'No matching entries. Answer from your general knowledge of UAE emergency procedures. Then call done_answering().',
            },
          };
        } catch (err) {
          console.error('[InquiryAgent] Knowledge base search failed:', err);
          return {
            toolName, args,
            result: {
              success: false,
              knowledge_base_context: 'Knowledge base temporarily unavailable. Answer from general emergency knowledge.',
              message: 'Search unavailable — answer from general knowledge. Then call done_answering().',
            },
          };
        }
      }

      // ─── done_answering ─────────────────────────────────────────────────
      if (toolName === 'done_answering') {
        const recommendedAgencies = context.transitionData.recommendedAgencies || [];
        const incidentSummary = args.incident_summary || context.transitionData.incidentSummary || '';

        console.log(
          `%c[InquiryAgent] done_answering | agencies: [${recommendedAgencies.join(', ')}] | hasIncident: ${recommendedAgencies.length > 0}`,
          'color: #ff9800; font-weight: bold'
        );

        if (recommendedAgencies.length > 0) {
          // Has agencies to report — go to image upload first
          return {
            toolName, args,
            result: { success: true, message: 'Routing to image upload agent before agency consent' },
            triggersTransition: true,
            transitionTo: 'image_upload_agent',
            transitionData: {
              recommendedAgencies,
              incidentSummary,
              confirmedAgencies: context.transitionData.confirmedAgencies || [],
            },
          };
        } else {
          // Informational question — go directly to closing
          return {
            toolName, args,
            result: { success: true, message: 'No agencies needed, routing to closing' },
            triggersTransition: true,
            transitionTo: 'closing_agent',
            transitionData: {
              confirmedAgencies: context.transitionData.confirmedAgencies || [],
            },
          };
        }
      }

      // ─── Backward-compat: handle old tool names ─────────────────────────
      if (toolName === 'move_to_closing' || toolName === 'back_to_menu') {
        return {
          toolName, args,
          result: { success: true },
          triggersTransition: true,
          transitionTo: 'closing_agent',
          transitionData: { confirmedAgencies: context.transitionData.confirmedAgencies || [] },
        };
      }

      if (toolName === 'escalate_to_agencies') {
        const recommendedAgencies: string[] = args.recommended_agencies || args.agencies || [];
        return {
          toolName, args,
          result: { success: true },
          triggersTransition: true,
          transitionTo: 'image_upload_agent',
          transitionData: {
            recommendedAgencies,
            incidentSummary: args.summary || '',
            confirmedAgencies: context.transitionData.confirmedAgencies || [],
          },
        };
      }

      if (toolName === 'request_human_handoff') {
        return {
          toolName, args,
          result: { success: true },
          triggersTransition: true,
          transitionTo: 'human_handoff_agent',
          transitionData: { confirmedAgencies: context.transitionData.confirmedAgencies || [] },
        };
      }

      return { toolName, args, result: { success: false, error: `Unknown tool: ${toolName}` } };
    },

    onActivate: async (context: AgentContext) => {
      const question = context.transitionData.pendingQuestion;
      const agencies = context.transitionData.recommendedAgencies || [];
      console.log(
        `[InquiryAgent] Activated | question: "${question || '(none)'}" | pre-detected agencies: [${agencies.join(', ')}]`
      );
    },
  };
}

export default createInquiryAgent;
